import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { CheckCircle2, Circle, AlertTriangle, Mail } from "lucide-react";
import { getClientOnboarding, markNutritionConsult, resendAppInvite, markParqCleared, getClientPhotos, appMediaUrl,
  listPhotoReviews, draftPhotoReview, editPhotoReview, sendPhotoReview, discardPhotoReview } from "@/lib/api";

const EATING_NOW = [["m1", "Meal 1"], ["m2", "Meal 2"], ["m3", "Meal 3"], ["snack", "Snacks"], ["drinks", "Drinks"]];
const GROUPS = [["protein", "Protein"], ["carb", "Carbs"], ["fat", "Fats"], ["veg", "Veg"], ["snack", "Snacks"], ["fruit", "Fruit"]];

/** A coach's view of one client's 6 Week Challenge onboarding: steps, health check, what they eat
 *  now next to their plan, their food picks, test attempts and the app invite. */
export default function OnboardingDialog({ client, canManage, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [photos, setPhotos] = useState([]);

  const load = useCallback(() => {
    getClientOnboarding(client.id).then(setData, (e) => toast.error(e?.response?.data?.detail || "Could not load onboarding"));
    getClientPhotos(client.id).then((r) => setPhotos(r.sets || []), () => setPhotos([]));
  }, [client.id]);
  useEffect(load, [load]);

  const resend = async () => {
    setBusy(true);
    try {
      const r = await resendAppInvite(client.id);
      toast.success(`Invite: ${r.invite.status.replace("_", " ")}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the invite");
    } finally {
      setBusy(false);
    }
  };
  const cleared = async () => {
    try { await markParqCleared(data.parq.id); toast.success("Marked as cleared by their doctor"); load(); } catch { toast.error("Could not update"); }
  };

  const consult = async (done) => {
    try {
      await markNutritionConsult(client.id, done);
      toast.success(done ? "Consult marked done: the test is unlocked in their app" : "Consult unmarked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const parq = data?.parq;
  const yes = parq ? (data.parq_questions || []).filter((q) => parq.answers?.[q.id]?.yes) : [];
  const ch = data?.challenge;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-100 max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="onboarding-dialog">
        <DialogHeader>
          <DialogTitle>Onboarding · {client.name}</DialogTitle>
          <DialogDescription>
            {data ? `${data.done} of ${data.total} steps done` : "Loading…"}
            {ch?.start_date ? ` · programme starts ${ch.start_date}${ch.phase === "pre_start" ? ` (in ${ch.days_to_start} days)` : ""}` : ""}
          </DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-5 text-sm">
            <section className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
              {data.steps.map((s) => (
                <div key={s.key} className="flex items-center gap-2">
                  {s.done ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Circle className="h-4 w-4 text-zinc-600" />}
                  <span className={s.done ? "text-zinc-200" : "text-zinc-500"}>{s.title}</span>
                  {s.progress && <span className="text-[11px] text-zinc-500">{s.progress}</span>}
                </div>
              ))}
            </section>

            <section>
              <div className="eyebrow mb-2">Health check</div>
              {!parq ? <div className="text-zinc-500">Not filled in yet.</div> : (
                <div className="space-y-2">
                  {!parq.any_yes ? <div className="text-emerald-300">No to every question · {parq.date}</div> : (
                    <>
                      {yes.map((q) => (
                        <div key={q.id} className="rounded-lg border border-amber-500/25 bg-amber-500/[0.05] p-3">
                          <div className="flex items-start gap-2 text-amber-200"><AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />{q.q}</div>
                          {parq.answers[q.id].areas?.length > 0 && (
                            <div className="mt-1 text-xs text-zinc-300">Where: {parq.answers[q.id].areas.map((a) => data.areas?.[a] || a).join(", ")}</div>
                          )}
                          {parq.answers[q.id].detail && <div className="mt-1 text-xs text-zinc-300">“{parq.answers[q.id].detail}”</div>}
                        </div>
                      ))}
                      <div className="text-xs text-zinc-500">
                        {parq.doctor_cleared_at ? `Doctor clearance noted ${parq.doctor_cleared_at.slice(0, 10)} (${parq.cleared_by || "member"}).`
                          : "Not cleared by a doctor yet. Nothing is paused; the member was told what to skip until then."}
                      </div>
                      {!parq.doctor_cleared_at && <Button size="sm" variant="outline" onClick={cleared}>Mark doctor clearance received</Button>}
                    </>
                  )}
                  {parq.past_injuries && <div className="text-xs text-zinc-300">Older injuries or surgeries: {parq.past_injuries}</div>}
                  <div className="text-[11px] text-zinc-600">Signed {parq.signed_name} · valid until {parq.valid_until}</div>
                </div>
              )}
            </section>

            <section className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="eyebrow mb-2">What they eat now</div>
                {data.eating_now ? EATING_NOW.map(([k, label]) => data.eating_now[k] ? (
                  <div key={k} className="mb-1"><span className="text-zinc-500">{label}: </span><span className="text-zinc-200">{data.eating_now[k]}</span></div>
                ) : null) : <div className="text-zinc-500">Not filled in yet.</div>}
              </div>
              <div>
                <div className="eyebrow mb-2">Their plan{data.plan?.confirmed_at ? "" : " (not saved yet)"}</div>
                {data.plan ? (
                  <>
                    {data.plan.meals.map((m) => (
                      <div key={m.key} className="mb-1">
                        <span className="text-zinc-500">{m.label.split(" · ")[0]}: </span>
                        <span className="text-zinc-200">{m.items.map((i) => `${i.name} ${i.amount}`).join(", ") || "—"}</span>
                      </div>
                    ))}
                    <div className="mt-1 text-xs text-zinc-500">
                      ≈ {data.plan.totals.kcal} kcal · P {data.plan.totals.protein_g} g · C {data.plan.totals.carbs_g} g · F {data.plan.totals.fat_g} g
                    </div>
                  </>
                ) : <div className="text-zinc-500">Not built yet.</div>}
              </div>
            </section>

            {photos.length > 0 && (
              <section>
                <div className="eyebrow mb-2">Progress photos</div>
                <div className="space-y-2">
                  {[photos[0], photos[photos.length - 1]].filter((p, i, a) => a.indexOf(p) === i).map((set) => (
                    <div key={set.date}>
                      <div className="mb-1 text-xs text-zinc-500">{set.date}</div>
                      <div className="flex gap-2">
                        {["front", "side", "back"].map((pose) => set.photos[pose] ? (
                          <a key={pose} href={appMediaUrl(set.photos[pose])} target="_blank" rel="noreferrer">
                            <img src={appMediaUrl(set.photos[pose])} alt={`${pose} ${set.date}`} className="h-32 w-24 rounded-md object-cover" />
                          </a>
                        ) : null)}
                      </div>
                    </div>
                  ))}
                </div>
                <PhotoNotes clientId={client.id} />
              </section>
            )}

            {Object.keys(data.picks || {}).length > 0 && (
              <section>
                <div className="eyebrow mb-2">Food picks</div>
                {GROUPS.map(([k, label]) => data.picks[k]?.length ? (
                  <div key={k} className="mb-1"><span className="text-zinc-500">{label}: </span><span className="text-zinc-200">{data.picks[k].join(", ")}</span></div>
                ) : null)}
              </section>
            )}

            <section className="grid gap-4 sm:grid-cols-2">
              <div>
                <div className="eyebrow mb-2">Nutrition consult</div>
                {data.consult?.at ? (
                  <div className="mb-3 text-zinc-300">Done {data.consult.at.slice(0, 10)}{data.consult.by ? ` · ${data.consult.by}` : ""}
                    {!data.test.passed_at && <button type="button" className="ml-2 text-xs text-zinc-500 underline" onClick={() => consult(false)}>undo</button>}
                  </div>
                ) : (
                  <div className="mb-3 space-y-2">
                    <div className="text-xs text-zinc-500">In person at the gym. The test covers: {(data.consult?.topics || []).join(", ")}.</div>
                    <Button size="sm" onClick={() => consult(true)} data-testid="consult-done">Consult done: unlock the test</Button>
                  </div>
                )}
                <div className="eyebrow mb-2">Nutrition test</div>
                {data.test.passed_at ? <div className="text-emerald-300">Passed {data.test.passed_at.slice(0, 10)}</div>
                  : data.test.attempts.length ? <div className="text-zinc-300">Not passed yet · best {data.test.best?.score}/{data.test.best?.of}</div>
                    : <div className="text-zinc-500">Not taken yet.</div>}
                {data.test.attempts.length > 0 && <div className="text-xs text-zinc-500">{data.test.attempts.length} attempt{data.test.attempts.length === 1 ? "" : "s"}</div>}
              </div>
              <div>
                <div className="eyebrow mb-2">App invite</div>
                {data.invite ? (
                  <div className="text-zinc-300">
                    {data.invite.status.replace(/_/g, " ")}{data.invite.sent_at ? ` · ${data.invite.sent_at.slice(0, 10)}` : ""}
                    {data.invite.gym_contact?.state === "ok" && <div className="text-xs text-zinc-500">Tagged in the gym&apos;s GoHighLevel</div>}
                  </div>
                ) : <div className="text-zinc-500">No invite (not sold into a challenge from FPA).</div>}
                {canManage && data.invite && (
                  <Button size="sm" variant="outline" className="mt-2" onClick={resend} disabled={busy}>
                    <Mail className="mr-1 h-3.5 w-3.5" /> {busy ? "Sending…" : "Send the invite again"}
                  </Button>
                )}
              </div>
            </section>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/** The AI's "what's changed" note on the progress photos: the coach edits it, then sends it in the
 *  member's chat as themselves, or discards it. Nothing reaches the member without a coach. */
function PhotoNotes({ clientId }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    listPhotoReviews(clientId).then((r) => {
      setData(r);
      const d = (r.reviews || []).find((x) => x.status === "draft");
      setText(d ? d.text : "");
    }, () => setData({ reviews: [], enabled: false }));
  }, [clientId]);
  useEffect(load, [load]);
  if (!data) return null;
  const draft = (data.reviews || []).find((x) => x.status === "draft");
  const sent = (data.reviews || []).filter((x) => x.status === "sent");
  const act = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 space-y-2">
      <div className="eyebrow">What's changed (AI draft for you to check)</div>
      {draft ? (
        <div className="space-y-2 rounded-lg border border-[#262626] p-3">
          <div className="text-xs text-zinc-500">
            {draft.from_date} vs {draft.to_date}
            {draft.comparable ? "" : " · the photos differ a lot (light, distance or pose): check before sending"}
          </div>
          {draft.capture_issues?.length > 0 && <div className="text-xs text-amber-300">Photo issues: {draft.capture_issues.join("; ")}</div>}
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={4}
            className="w-full rounded-md border border-[#262626] bg-[#0a0a0a] p-2 text-sm text-zinc-100" data-testid="photo-note-text" />
          <div className="flex gap-2">
            <Button size="sm" disabled={busy || !text.trim()} data-testid="photo-note-send"
              onClick={() => act(async () => { if (text.trim() !== draft.text) await editPhotoReview(draft.id, text.trim()); await sendPhotoReview(draft.id); }, "Sent in their chat")}>
              Send in their chat
            </Button>
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => act(() => discardPhotoReview(draft.id), "Discarded")}>Discard</Button>
          </div>
        </div>
      ) : data.enabled ? (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => act(() => draftPhotoReview(clientId), "Draft ready")}>
          Draft a note comparing first and latest photos
        </Button>
      ) : <div className="text-xs text-zinc-500">AI photo notes are switched off.</div>}
      {sent.map((r) => (
        <div key={r.id} className="text-xs text-zinc-400">Sent {r.sent_at?.slice(0, 10)} by {r.sent_by}: “{r.text}”</div>
      ))}
    </div>
  );
}
